import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { VendorResponse } from '@signalvote/shared';

interface PeopleFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  roleFilter: string | null;
  onRoleFilterChange: (value: string | null) => void;
  statusFilter: string | null;
  onStatusFilterChange: (value: string | null) => void;
  typeFilter: string | null;
  onTypeFilterChange: (value: string | null) => void;
  vendorFilter: string | null;
  onVendorFilterChange: (value: string | null) => void;
  vendors: VendorResponse[];
}

export function PeopleFilters({
  searchQuery,
  onSearchChange,
  roleFilter,
  onRoleFilterChange,
  statusFilter,
  onStatusFilterChange,
  typeFilter,
  onTypeFilterChange,
  vendorFilter,
  onVendorFilterChange,
  vendors,
}: PeopleFiltersProps) {
  const hasActiveFilter = !!searchQuery || !!roleFilter || !!statusFilter || !!typeFilter || !!vendorFilter;

  const clearAllFilters = () => {
    onSearchChange('');
    onRoleFilterChange(null);
    onStatusFilterChange(null);
    onTypeFilterChange(null);
    onVendorFilterChange(null);
  };

  return (
    <div className="glass-surface rounded-lg border shadow-card p-3">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 rounded-full"
          />
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Select
            value={roleFilter ?? 'all'}
            onValueChange={(value) => onRoleFilterChange(value === 'all' ? null : value)}
          >
            <SelectTrigger className={cn(
              'w-[160px] rounded-full shadow-card hover:shadow-card-hover transition-all duration-200',
              roleFilter && 'ring-2 ring-primary'
            )}>
              <SelectValue placeholder="All Permissions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Permissions</SelectItem>
              <SelectItem value="Owner">Owner</SelectItem>
              <SelectItem value="FullAdmin">Full Admin</SelectItem>
              <SelectItem value="FieldDirector">Field Director</SelectItem>
              <SelectItem value="Volunteer">Volunteer</SelectItem>
              <SelectItem value="ViewOnly">View Only</SelectItem>
              <SelectItem value="Custom">Custom</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={statusFilter ?? 'all'}
            onValueChange={(value) => onStatusFilterChange(value === 'all' ? null : value)}
          >
            <SelectTrigger className={cn(
              'w-[130px] rounded-full shadow-card hover:shadow-card-hover transition-all duration-200',
              statusFilter && 'ring-2 ring-primary'
            )}>
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={typeFilter ?? 'all-type'}
            onValueChange={(value) => {
              const newType = value === 'all-type' ? null : value;
              onTypeFilterChange(newType);
              // Clear vendor filter when switching to Volunteer
              if (newType === 'Volunteer') {
                onVendorFilterChange(null);
              }
            }}
            data-testid="type-filter"
          >
            <SelectTrigger className={cn(
              'w-[140px] rounded-full shadow-card hover:shadow-card-hover transition-all duration-200',
              typeFilter && 'ring-2 ring-primary'
            )}>
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all-type">All Types</SelectItem>
              <SelectItem value="Volunteer">Volunteer</SelectItem>
              <SelectItem value="Paid">Paid</SelectItem>
            </SelectContent>
          </Select>
          {typeFilter !== 'Volunteer' && (
            <Select
              value={vendorFilter ?? 'all-vendor'}
              onValueChange={(value) => onVendorFilterChange(value === 'all-vendor' ? null : value)}
              data-testid="vendor-filter"
            >
              <SelectTrigger className={cn(
                'w-[160px] rounded-full shadow-card hover:shadow-card-hover transition-all duration-200',
                vendorFilter && 'ring-2 ring-primary'
              )}>
                <SelectValue placeholder="All Vendors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-vendor">All Vendors</SelectItem>
                {vendors.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {hasActiveFilter && (
            <Button variant="ghost" size="sm" onClick={clearAllFilters} className="rounded-full">
              <X className="h-3.5 w-3.5 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
